import { isRouteErrorResponse } from 'react-router-dom';

export interface ErrorPresentation {
  title: string;
  message: string;
  details: string | null;
}

export function getErrorPresentation(error: unknown): ErrorPresentation {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: 'We couldn’t find this page',
        message: 'The page may have moved, been removed, or you may not have access to it.',
        details: `${error.status} ${error.statusText}${error.data ? `\n${stringifyDetail(error.data)}` : ''}`,
      };
    }

    if (error.status === 403) {
      return {
        title: 'You don’t have access to this page',
        message: 'Your role does not currently include permission to view this area. Contact a workspace administrator if you need access.',
        details: `${error.status} ${error.statusText}${error.data ? `\n${stringifyDetail(error.data)}` : ''}`,
      };
    }

    return {
      title: 'This page couldn’t be loaded',
      message: 'The server couldn’t complete the request. Try again, or return to the dashboard if the problem continues.',
      details: `${error.status} ${error.statusText}${error.data ? `\n${stringifyDetail(error.data)}` : ''}`,
    };
  }

  if (error instanceof Error) {
    return {
      title: 'This page ran into a problem',
      message: 'We couldn’t finish loading this view. Try again, or return to the dashboard and continue working.',
      details: error.stack ?? error.message,
    };
  }

  return {
    title: 'This page ran into a problem',
    message: 'An unexpected issue prevented this view from loading. Try again in a moment.',
    details: stringifyDetail(error),
  };
}

function stringifyDetail(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
